import DialogCloseButton from '@components/common/dialog/DialogCloseButton';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import { Content } from '@components/common/layout/Content';
import { Column } from '@components/common/layoutUtils';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import colors from '@utils/colors';
import { smallModalStyle, popupEdgeCSS } from '@utils/popupUtils';
import storage from '@utils/storage';
import { LargeText, RegularText, SmallText } from '@utils/typography';
import { useState } from 'react';
import Popup from 'reactjs-popup';
import styled from 'styled-components';

const MOBILE_APP_QR_CODE_URL = 'https://s3.us-east-2.amazonaws.com/public.shovel.img/shovel-mobile-app-qr-code.svg';

const MobileAppPromo = () => {
  const [open, setOpen] = useState(false);
  const [isEverOpened, setIsEverOpened] = useState(storage.isMobilePromoOpened());

  return (
    <>
      <TippyTooltip
        target={
          <Item
            onClick={() => {
              setOpen(true);
              if (!isEverOpened) {
                storage.setMobilePromoOpened();
                setIsEverOpened(true);
              }
            }}
          >
            <MaterialIcon className={'navigation-icon'} name={'smartphone'} size={20} />
            {!isEverOpened && <NeverOpenedIndicator />}
          </Item>
        }
        position={'right'}
        content={'Mobile App'}
      />
      <Dialog
        open={open}
        contentStyle={smallModalStyle}
        lockScroll={false}
        closeOnEscape={true}
        closeOnDocumentClick={true}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Content>
          <CloseButton onClick={() => setOpen(false)} />
          <MobileAppDialogContent qrImageUrl={MOBILE_APP_QR_CODE_URL} />
        </Content>
      </Dialog>
    </>
  );
};

export default MobileAppPromo;

type MobileAppDialogContentProps = {
  qrImageUrl: string;
};

export const MobileAppDialogContent: React.FC<MobileAppDialogContentProps> = ({ qrImageUrl }) => {
  return (
    <Wrapper>
      <Header>
        <Title strong>Use Shovel on your phone</Title>
      </Header>

      <Text>Download our mobile app to plan and manage your tasks on the go. Your data stays in sync with the web app.</Text>

      <QrSection>
        <QrImage src={qrImageUrl} alt="QR code to download the mobile app" />
        <Hint>Scan this code with your phone&apos;s camera to download the app.</Hint>
      </QrSection>
    </Wrapper>
  );
};

const Dialog = styled(Popup)`
  &-content {
    background: ${({ theme }) => theme.background} !important;
    ${popupEdgeCSS};
  }

  &-overlay {
    background: ${({ theme }) => theme.modalOverlayColor} !important;
  }
`;

const CloseButton = styled(DialogCloseButton)`
  top: 10px;
  right: -6px;
`;

const Wrapper = styled(Column)`
  padding: 20px 10px 0 10px;
  max-width: 420px;
  gap: 10px;
`;

const Header = styled.div`
  margin-bottom: 16px;
  text-align: center;
`;

const Title = styled(LargeText)`
  margin: 0;
`;

const Text = styled(RegularText)`
  line-height: 1.5;
`;

const QrSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const QrImage = styled.img`
  width: 180px;
  height: 180px;
  max-width: 100%;
`;

const Hint = styled(SmallText)`
  margin-top: 8px;
  text-align: center;
  width: 240px;
`;

const Item = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 5px;
  background-color: inherit;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.backgroundDark};

    .navigation-icon {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

const NeverOpenedIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: ${colors.negative};
  border-radius: 50%;
`;
